// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface PressMembership {
  id: string;
  slug: string;
  name: string;
  description?: string;
  price_cents: number;
  billing_type: string;
  is_addon: boolean;
  audience_type: string;
  stripe_price_id?: string;
}

interface ParentMembership {
  membership_slug: string;
  is_active: boolean;
}

export const usePressMemberships = () => {
  const { profile } = useAuth();
  const [pressMemberships, setPressMemberships] = useState<PressMembership[]>([]);
  const [parentMemberships, setParentMemberships] = useState<ParentMembership[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!profile?.id) {
        console.log('🚫 No profile ID available, skipping press memberships fetch');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        console.log('🔍 Fetching press memberships for parent dashboard...', {
          profileId: profile.id,
          profileRole: profile.role
        });

        // Fetch all active press membership addons INCLUDING stripe_price_id
        const { data: memberships, error: membershipsError } = await supabase
          .from('memberships')
          .select('id, slug, name, description, price_cents, billing_type, is_addon, audience_type, stripe_price_id')
          .eq('is_active', true)
          .eq('is_addon', true)
          .eq('audience_type', 'press')
          .order('price_cents', { ascending: true });

        if (membershipsError) {
          console.error('❌ Error fetching press memberships:', membershipsError);
          throw membershipsError;
        }

        // ✅ DETAILED LOGGING: Log each membership with its stripe_price_id
        console.log('📋 Raw membership data from Supabase:', memberships);
        
        if (memberships && memberships.length > 0) {
          memberships.forEach((membership, index) => {
            console.log(`🔍 [MEMBERSHIP-${index}] Details:`, {
              id: membership.id,
              slug: membership.slug,
              name: membership.name,
              price_cents: membership.price_cents,
              is_addon: membership.is_addon,
              audience_type: membership.audience_type,
              stripe_price_id: membership.stripe_price_id,
              stripe_price_id_type: typeof membership.stripe_price_id,
              stripe_price_id_defined: membership.stripe_price_id !== undefined,
              stripe_price_id_null: membership.stripe_price_id === null,
              stripe_price_id_empty: membership.stripe_price_id === ''
            });
          });
        }

        console.log('📋 Found press memberships:', {
          count: memberships?.length || 0,
          memberships: memberships?.map(m => ({
            id: m.id,
            slug: m.slug,
            name: m.name,
            price_cents: m.price_cents,
            is_addon: m.is_addon,
            audience_type: m.audience_type,
            stripe_price_id: m.stripe_price_id,
            hasStripePrice: !!m.stripe_price_id
          }))
        });
        setPressMemberships(memberships || []);

        // Fetch parent's current memberships
        console.log('🔍 Fetching parent current memberships for profile:', profile.id);
        
        const { data: currentMemberships, error: currentError } = await supabase
          .from('profile_memberships')
          .select('membership_slug, is_active')
          .eq('profile_id', profile.id);

        if (currentError) {
          console.error('❌ Error fetching parent memberships:', currentError);
          throw currentError;
        }

        console.log('💳 Parent current memberships:', {
          profileId: profile.id,
          count: currentMemberships?.length || 0,
          memberships: currentMemberships?.map(m => ({
            membership_slug: m.membership_slug,
            is_active: m.is_active
          }))
        });
        setParentMemberships(currentMemberships || []);

        // Cross-reference to see which press memberships are already owned
        const ownedSlugs = (currentMemberships || [])
          .filter(m => m.is_active)
          .map(m => m.membership_slug);
        
        const availablePress = memberships?.filter(m => !ownedSlugs.includes(m.slug)) || [];
        const ownedPress = memberships?.filter(m => ownedSlugs.includes(m.slug)) || [];

        console.log('🎯 Press membership analysis:', {
          totalPressOptions: memberships?.length || 0,
          alreadyOwned: ownedPress.length,
          availableForPurchase: availablePress.length,
          ownedSlugs,
          ownedPress: ownedPress.map(p => ({ slug: p.slug, name: p.name })),
          availablePress: availablePress.map(p => ({ 
            slug: p.slug, 
            name: p.name, 
            stripe_price_id: p.stripe_price_id,
            hasStripePrice: !!p.stripe_price_id
          }))
        });

      } catch (err) {
        console.error('❌ Error in usePressMemberships:', err);
        setError(err instanceof Error ? err.message : 'Failed to load press memberships');
        toast.error('Failed to load press memberships');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [profile?.id]);

  // Helper function to check if parent already has a specific membership
  const hasMembership = (membershipSlug: string) => {
    const activeMembership = parentMemberships.find(
      m => m.membership_slug === membershipSlug && m.is_active
    );
    const hasIt = !!activeMembership;
    console.log(`🔍 Checking membership ${membershipSlug}:`, {
      hasIt: hasIt ? 'Already purchased' : 'Available for purchase',
      activeMembership,
      allParentMemberships: parentMemberships
    });
    return hasIt;
  };

  return {
    pressMemberships,
    parentMemberships,
    hasMembership,
    isLoading,
    error,
  };
};
